// @ts-nocheck
/**
 * Template V2 — Template Class definitions (DEC-066).
 *
 * Classes are portable behavior packages: required categories, lifecycle/API
 * bindings, and a class-local script/expression library. Project-authored
 * packages embed into TemplateDocument.classes on template export and flatten
 * onto elements at load/compile. Categories remain data-only.
 */

import type { CategoryBundle } from './category';
import type { LocalizedText } from './metadata';
import type { DataRef } from './binding';
import type { TemplateActionDefinition } from './action';
import type {
  EventHandler,
  ScriptEventBinding,
  ScriptEventName,
  TemplateScriptLibraryInline,
} from './script';

/**
 * Types allowed on class-authored variable defaults (DEC-067).
 * Subset of DEC-049 item variable types. `list` seeds as empty `[]` only.
 */
export const CLASS_VARIABLE_DEFAULT_TYPES = [
  'string',
  'integer',
  'float',
  'boolean',
  'color',
  'datetime',
  'itemRef',
  'list',
] as const;

export type ClassVariableDefaultType = (typeof CLASS_VARIABLE_DEFAULT_TYPES)[number];

const CLASS_VARIABLE_DEFAULT_TYPE_SET: ReadonlySet<string> = new Set(CLASS_VARIABLE_DEFAULT_TYPES);

export function isClassVariableDefaultType(type: string): type is ClassVariableDefaultType {
  return CLASS_VARIABLE_DEFAULT_TYPE_SET.has(type);
}

/**
 * Creation-time seed for DEC-049 item variables, authored on a Template Class.
 * Applied in creation phase 1 before `onCreated` / headless `onFirstMount` (DEC-067).
 */
export interface ClassVariableDefault {
  /** Item-variable key (`ITEM_VARIABLE_POLICY.keyPattern`). */
  key: string;
  /** Typed bag entry type. */
  type: ClassVariableDefaultType;
  /**
   * Default written via `writeVariable`.
   * Scalars for most types; `null` for unset `itemRef`; empty array `[]` only for `list`.
   */
  value: string | number | boolean | null | readonly unknown[];
}
/**
 * Lifecycle events a Template Class may bind.
 * Layout/visual/canvas/SVG/component triggers are excluded.
 */
export const CLASS_SCRIPT_EVENT_NAMES = [
  'onCreated',
  'onDeleted',
  'onFirstMount',
  'onItemParentChange',
  'onItemAdded',
  'onItemRemoved',
  'onEdited',
  'onGraphChange',
  'onCloudSyncApplied',
] as const satisfies readonly ScriptEventName[];

/** Event names allowed on {@link TemplateClassDefinition} bindings. */
export type ClassScriptEventName = (typeof CLASS_SCRIPT_EVENT_NAMES)[number];

const CLASS_SCRIPT_EVENT_NAME_SET: ReadonlySet<string> = new Set(CLASS_SCRIPT_EVENT_NAMES);

/** Type guard for class-allowed lifecycle event names. */
export function isClassScriptEventName(event: string): event is ClassScriptEventName {
  return CLASS_SCRIPT_EVENT_NAME_SET.has(event);
}

/** How an element-owned binding composes with attached class handlers on the same event. */
export type ClassCompositionMode = 'before' | 'after' | 'replace';

/**
 * Event binding owned by a class. Same shape as element bindings, plus role and
 * optional provenance stamped during flatten.
 */
export interface ClassScriptEventBinding extends ScriptEventBinding {
  /** Lifecycle event — must be a {@link ClassScriptEventName}. */
  event: ClassScriptEventName;
  /**
   * Stable author-visible role for inheritance overwrite (DEC-066 addendum).
   * Child class binding with the same role replaces the ancestor binding.
   * When omitted, flatten falls back to {@link ScriptEventBinding.id}.
   */
  bindingRoleId?: string;
  /**
   * Set during flatten to the class that contributed this binding.
   * Authoring documents omit this; runtime may retain it for diagnostics.
   */
  sourceClassId?: string;
}

/** Lifecycle + API bindings for a Template Class. */
export interface TemplateClassBindings {
  /** Class-owned lifecycle bindings (allowed event subset only). */
  events?: ClassScriptEventBinding[];
  /**
   * Scripts exposed on items whose elements attach this class (DEC-057).
   * Script handlers only (`kind: 'script'`).
   */
  api?: EventHandler[];
}

/**
 * A reusable template class: categories + behavior package + local library.
 *
 * Inheritance is single-parent via {@link extendsClassId}. Only classes chain.
 * Element trigger wires compose via {@link ScriptEventBinding.classComposition}.
 */
export interface TemplateClassDefinition {
  /** Unique identifier within the package / template embed. */
  id: string;
  /** Optional localized display name for editor tooling. */
  displayName?: LocalizedText;
  /** Optional localized description. */
  description?: LocalizedText;
  /**
   * Single parent class id. Acyclic. Parent bindings expand before this class;
   * a child event binding with the same `bindingRoleId` (or `id`) overwrites
   * the ancestor binding; a child API handler with the same `scriptId` replaces
   * the ancestor entry.
   */
  extendsClassId?: string;
  /**
   * Category definition ids this class requires.
   * Merged into `element.categoryIds` when the class is attached.
   */
  categoryIds: string[];
  /** Lifecycle and API bindings for this class (not including ancestors). */
  bindings?: TemplateClassBindings;
  /**
   * Typed item-variable defaults seeded at item creation (DEC-067).
   * Not flattened onto the element; resolved at create from attach order + ancestry.
   * Later classes / child classes overwrite the same key.
   */
  variableDefaults?: ReadonlyArray<ClassVariableDefault>;
  /**
   * Class-local script/expression library.
   * The same id may appear in `document.scripts` and multiple class libraries when
   * the definition is identical (shared / dual-export). Colliding ids with different
   * bodies remain illegal (`semantic.duplicate_script_id` / `semantic.duplicate_expression_id`).
   */
  library?: TemplateScriptLibraryInline;
}

/** Metadata for a standalone class package document. */
export interface ClassPackageMetadata {
  /** Stable unique identifier for this package. */
  packageId: string;
  /** Human-readable display name keyed by locale. */
  displayName: LocalizedText;
  /** Optional description keyed by locale. */
  description?: LocalizedText;
  /** Author or organization name. */
  author?: string;
  /** Primary class id in {@link TemplateClassPackage.classes}. */
  primaryClassId: string;
  /** ISO 8601 creation timestamp. */
  createdAt?: string;
  /** ISO 8601 last-updated timestamp. */
  updatedAt?: string;
}

/**
 * Standalone portable class package (DEC-066 addendum).
 * Editor import/export unit; templates embed used classes into TemplateDocument.classes.
 */
export interface ClassPackageEditFilter {
  /** Edit-filter node id. */
  id: string;
  displayName?: string;
  watchRefs?: DataRef[];
  /** Class that wires into this filter via a lifecycle trigger. */
  sourceClassId: string;
  /** Lifecycle event on the class trigger rail. */
  event: ClassScriptEventName;
  /** Handlers reached after the filter (script or action). */
  outputs: Array<
    | { kind: 'script'; scriptId: string }
    | { kind: 'action'; actionId: string }
  >;
}

export interface TemplateClassPackage {
  /** Discriminant — always 'classPackage'. */
  kind: 'classPackage';
  /** Schema version — always 2. */
  version: 2;
  /** Package metadata. */
  metadata: ClassPackageMetadata;
  /** Category bundles required by the classes in this package (incl. category parents). */
  categories: CategoryBundle[];
  /**
   * Class definitions (primary + ancestors as needed).
   * Each should carry its `library` for scripts/expressions.
   */
  classes: TemplateClassDefinition[];
  /** Actions referenced by class bindings or class-owned edit-filter outputs. */
  actions?: TemplateActionDefinition[];
  /** Edit filters wired on class triggers (editor authoring; flatten uses binding.watchRefs). */
  editFilters?: ClassPackageEditFilter[];
}

/** Role key used for class event merge (role preferred, else binding id). */
export function classBindingRoleKey(binding: {
  id: string;
  bindingRoleId?: string;
}): string {
  const role = binding.bindingRoleId?.trim();
  return role && role.length > 0 ? role : binding.id;
}
